import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessDataContext = createContext();

export const useBusinessData = () => {
  const context = useContext(BusinessDataContext);
  if (!context) {
    throw new Error('useBusinessData must be used within a BusinessDataProvider');
  }
  return context;
};

export const BusinessDataProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for business data endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

  // Determine the correct base URL based on login type
  const getBusinessDataBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/business-data/api/v1/business-data`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/business-data/api/v1/business-data`;
    } else {
      throw new Error('Invalid login type for business data access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }

    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access business data');
    }
  }, [loginType]);

  // ------------------------------------------------------------------
  // GET REQUESTS (READ)
  // ------------------------------------------------------------------

  // Get all businesses with pagination and filters
  const getBusinesses = useCallback(async (options = {}) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();

      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.dateTo) queryParams.append('date_to', options.dateTo);
      if (options.sortBy) queryParams.append('sort_by', options.sortBy);
      if (options.sortDir) queryParams.append('sort_dir', options.sortDir);
      if (options.category) queryParams.append('category', options.category);
      if (options.year) queryParams.append('year', options.year);
      if (options.verifiedEmail !== undefined) queryParams.append('verified_email', options.verifiedEmail);
      if (options.verifiedPhone !== undefined) queryParams.append('verified_phone', options.verifiedPhone);
      if (options.search) queryParams.append('search', options.search);
      if (options.dateFrom) queryParams.append('date_from', options.dateFrom);
      if (options.dateTo) queryParams.append('date_to', options.dateTo);

      const baseUrl = getBusinessDataBaseUrl();
      const url = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;
      const response = await makeAuthenticatedRequest(url);

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch businesses';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessDataBaseUrl]);

  // Get business by ID
  const getBusinessById = useCallback(async (businessId) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBusinessDataBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/${businessId}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get business';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessDataBaseUrl]);

  // Get business by email
  const getBusinessByEmail = useCallback(async (email) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBusinessDataBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/email/${encodeURIComponent(email)}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get business by email';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessDataBaseUrl]);

  const getBusinessLogo = useCallback(async (registryNumber) => {
    validateAccess();
    try {
      const baseUrl = getBusinessDataBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/logo`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      console.error('Failed to get logo:', err);
      return null;
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessDataBaseUrl]);

  const updateBusinessLogo = useCallback(async (registryNumber, formData) => {
    validateAccess();
    setLoading(true);
    try {
      const baseUrl = getBusinessDataBaseUrl();

      const response = await makeAuthenticatedRequest(
        `${baseUrl}/registry/${encodeURIComponent(registryNumber)}/logo`,
        {
          method: 'POST',
          body: formData,
          // CRITICAL FIX: 
          // We set Content-Type to undefined. This tells your auth wrapper 
          // (and the browser) NOT to set "application/json".
          // The browser will detect FormData and automatically set:
          // "Content-Type: multipart/form-data; boundary=----WebKitFormBoundary..."
          headers: {
            'Content-Type': undefined
          }
        }
      );

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update logo';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessDataBaseUrl]);


  // Get business by phone number
  const getBusinessByPhone = useCallback(async (phone) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBusinessDataBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/phone/${encodeURIComponent(phone)}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get business by phone';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessDataBaseUrl]);

  // Get business by registry number
  const getBusinessByRegistryNumber = useCallback(async (registryNumber) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBusinessDataBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get business by registry number';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessDataBaseUrl]);

  // Get business statistics
  const getBusinessStats = useCallback(async () => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBusinessDataBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/stats`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get business statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessDataBaseUrl]);

  // Get business categories
  const getBusinessCategories = useCallback(async () => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBusinessDataBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/categories`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get business categories';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessDataBaseUrl]);

  // ------------------------------------------------------------------
  // PATCH REQUESTS (UPDATE) - NEW SECTION
  // ------------------------------------------------------------------

  // Update business verification status
  const updateBusinessVerification = useCallback(async (registryNumber, verificationData) => {
    validateAccess();

    // Validation to ensure we are sending correct data keys
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    // Ensure data structure matches Backend Expectation: { verified_email: bool, verified_phone: bool }
    if (verificationData.verified_email === undefined && verificationData.verified_phone === undefined) {
      throw new Error('At least one verification field (verified_email or verified_phone) is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBusinessDataBaseUrl();

      // Call the NEW endpoint we just created in Go
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            verified_email: verificationData.verified_email,
            verified_phone: verificationData.verified_phone
          })
        }
      );

      const result = await handleApiResponse(response);
      return result.verification_status; // Return the specific status object
    } catch (err) {
      const errorMessage = err.message || 'Failed to update verification status';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessDataBaseUrl]);


  // ------------------------------------------------------------------
  // UTILITIES & HELPERS
  // ------------------------------------------------------------------

  // Search businesses with advanced filtering
  const searchBusinesses = useCallback(async (searchTerm, filters = {}) => {
    validateAccess();

    const searchOptions = {
      search: searchTerm,
      limit: filters.limit || 50,
      offset: filters.offset || 0,
      ...filters
    };

    return await getBusinesses(searchOptions);
  }, [getBusinesses, validateAccess]);

  // Get businesses by verification status
  const getBusinessesByVerificationStatus = useCallback(async (emailVerified = null, phoneVerified = null, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      verifiedEmail: emailVerified,
      verifiedPhone: phoneVerified,
    };

    return await getBusinesses(filterOptions);
  }, [getBusinesses, validateAccess]);

  // Get businesses by category
  const getBusinessesByCategory = useCallback(async (category, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      category: category,
    };

    return await getBusinesses(filterOptions);
  }, [getBusinesses, validateAccess]);

  // Get businesses by date range
  const getBusinessesByDateRange = useCallback(async (dateFrom, dateTo, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      dateFrom: dateFrom,
      dateTo: dateTo,
    };

    return await getBusinesses(filterOptions);
  }, [getBusinesses, validateAccess]);

  // Get recent registrations (last 30 days)
  const getRecentRegistrations = useCallback(async (options = {}) => {
    validateAccess();

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const filterOptions = {
      ...options,
      dateFrom: thirtyDaysAgo.toISOString().split('T')[0], // YYYY-MM-DD format
    };

    return await getBusinesses(filterOptions);
  }, [getBusinesses, validateAccess]);

  // Export business data (prepare data for CSV/Excel export)
  const exportBusinessData = useCallback(async (filters = {}) => {
    validateAccess();

    // Get all businesses without pagination for export
    const exportOptions = {
      ...filters,
      limit: 10000, // Large limit for export
      offset: 0,
    };

    const result = await getBusinesses(exportOptions);

    // Transform data for export
    const exportData = result.businesses.map(business => ({
      'Business ID': business.id,
      'Business Name': business.business_name,
      'Business Owner': business.business_owner,
      'Email': business.business_email,
      'Category': business.business_category || 'N/A',
      'Email Verified': business.verified_email ? 'Yes' : 'No',
      'Phone Verified': business.verified_phone ? 'Yes' : 'No',
      'Registration Date': new Date(business.registration_date).toLocaleDateString(),
    }));

    return {
      data: exportData,
      totalCount: result.total_count,
      filename: `business_registrations_${new Date().toISOString().split('T')[0]}.csv`
    };
  }, [getBusinesses, validateAccess]);

  // Health check for business data service
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const healthUrl = loginType === 'master'
        ? `${API_BASE_URL}/master/business-data/health`
        : `${API_BASE_URL}/admin/business-data/health`;

      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // Validation helpers
  const validateBusinessFilters = useCallback((filters) => {
    const errors = [];

    if (filters.year && (filters.year < 1900 || filters.year > new Date().getFullYear())) {
      errors.push({ field: 'year', message: 'Invalid year provided' });
    }

    if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
      errors.push({ field: 'limit', message: 'Limit must be between 1 and 1000' });
    }

    if (filters.offset && filters.offset < 0) {
      errors.push({ field: 'offset', message: 'Offset must be non-negative' });
    }

    if (filters.dateFrom && filters.dateTo && new Date(filters.dateFrom) > new Date(filters.dateTo)) {
      errors.push({ field: 'dateRange', message: 'Date from must be before date to' });
    }

    return errors;
  }, []);

  // Format business data for display
  const formatBusinessData = useCallback((business) => {
    return {
      ...business,
      formattedRegistrationDate: new Date(business.registration_date).toLocaleDateString(),
      verificationStatus: business.verified_email && business.verified_phone ? 'Fully Verified' :
        business.verified_email || business.verified_phone ? 'Partially Verified' : 'Unverified',
      displayName: `${business.business_name} (${business.business_owner})`,
    };
  }, []);

  // Get summary statistics for dashboard
  const getDashboardStats = useCallback(async () => {
    validateAccess();

    try {
      const [stats, categories, recentRegistrations] = await Promise.all([
        getBusinessStats(),
        getBusinessCategories(),
        getRecentRegistrations({ limit: 10 })
      ]);

      return {
        totalBusinesses: stats.total_businesses,
        verifiedBusinesses: stats.verified_businesses,
        unverifiedBusinesses: stats.unverified_businesses,
        recentRegistrations: stats.recent_registrations,
        totalCategories: categories.count,
        topCategories: categories.categories.slice(0, 5),
        latestRegistrations: recentRegistrations.businesses,
        accessLevel: loginType, // Include access level info
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get dashboard statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getBusinessStats, getBusinessCategories, getRecentRegistrations, validateAccess, loginType]);

  // Get current endpoint info for debugging
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master'
        ? `${API_BASE_URL}/master/business-data/api/v1/business-data`
        : `${API_BASE_URL}/admin/business-data/api/v1/business-data`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master'
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, API_BASE_URL]);

  const value = {
    // State
    loading,
    error,

    // Core Actions (Read)
    getBusinesses,
    getBusinessById,
    getBusinessByEmail,
    getBusinessByPhone,
    getBusinessByRegistryNumber,
    getBusinessStats,
    getBusinessCategories,
    getBusinessLogo,

    // Core Actions (Update) - ADDED
    updateBusinessVerification,

    // Search and Filter Actions
    searchBusinesses,
    getBusinessesByVerificationStatus,
    getBusinessesByCategory,
    getBusinessesByDateRange,
    getRecentRegistrations,

    // Utility Actions
    exportBusinessData,
    checkHealth,
    getDashboardStats,
    updateBusinessLogo,


    // Helpers
    clearError,
    validateBusinessFilters,
    formatBusinessData,
    getEndpointInfo,

    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasBusinessDataAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <BusinessDataContext.Provider value={value}>
      {children}
    </BusinessDataContext.Provider>
  );
};